import { useCallback, useEffect, useMemo, useState } from 'react';

import Tooltip from 'components/tooltip';
import Notification from 'components/notification';
import {
  ACCOUNT_PLAYLIST_ERROR_MESSAGE,
  ACCOUNT_PLAYLIST_MESSAGES,
  ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS,
} from 'constants/playlists';
import {
  createAccountPlaylistToggleStore,
  AccountPlaylistState,
} from './AccountPlaylistToggle.state';
import { Wrapper, Label, Badge, BadgeIcon, Row, ToggleSwitch } from './styles';

export type AccountPlaylistToggleState = {
  isAccountPlaylist: boolean;
  isCoolingDown: boolean;
  cooldownEndsAt: number | null;
  cooldownSecondsRemaining: number;
  isLoading: boolean;
};

type Props = {
  playlistId?: string;
  initialIsAccountPlaylist?: boolean;
  isAccountOwner?: boolean;
  isTeamManager?: boolean;
  onRefresh?: () => Promise<void> | void;
  onStateChange?: (state: AccountPlaylistToggleState) => void;
  labelOn?: string;
  labelOff?: string;
  size?: 'default' | 'compact';
  className?: string;
  waitForRefresh?: boolean;
};

const AccountPlaylistToggle = ({
  playlistId,
  initialIsAccountPlaylist = false,
  isAccountOwner,
  isTeamManager,
  onRefresh,
  onStateChange,
  labelOn = 'Assigned to account',
  labelOff = 'Assign to account',
  className,
}: Props): JSX.Element => {
  const [isChecked, setIsChecked] = useState<boolean>(initialIsAccountPlaylist);
  const [loading, setLoading] = useState<boolean>(false);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [cooldownActive, setCooldownActive] = useState<boolean>(false);
  const [cooldownSecondsRemaining, setCooldownSecondsRemaining] = useState<number>(0);
  const accountPlaylistToggleStore = useMemo(() => createAccountPlaylistToggleStore(), []);

  const editable = Boolean(isAccountOwner);
  const readOnly = Boolean(isTeamManager) && !editable;

  useEffect(() => {
    setIsChecked(initialIsAccountPlaylist);
  }, [initialIsAccountPlaylist, playlistId]);

  useEffect(() => {
    if (!cooldownEndsAt) {
      setCooldownActive(false);
      setCooldownSecondsRemaining(0);
      return;
    }
    const now = Date.now();
    if (cooldownEndsAt <= now) {
      setCooldownActive(false);
      setCooldownSecondsRemaining(0);
      return;
    }

    setCooldownActive(true);
    const initialSeconds = Math.ceil((cooldownEndsAt - now) / 1000);
    setCooldownSecondsRemaining(initialSeconds);
    const interval = window.setInterval(() => {
      const remainingMs = cooldownEndsAt - Date.now();
      if (remainingMs <= 0) return;
      setCooldownSecondsRemaining(Math.ceil(remainingMs / 1000));
    }, 1000);
    const timeout = window.setTimeout(() => {
      setCooldownActive(false);
      setCooldownSecondsRemaining(0);
      window.clearInterval(interval);
    }, cooldownEndsAt - now);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [cooldownEndsAt]);

  const notifyParent = useCallback(
    (state: AccountPlaylistToggleState) => {
      onStateChange?.(state);
    },
    [onStateChange]
  );

  const applyState = useCallback((state: AccountPlaylistState) => {
    const cooldown = state.cooldown_ends_at ?? null;
    setIsChecked(state.is_account_playlist);
    setCooldownEndsAt(cooldown);
  }, []);

  // Keep UI aligned with the local store (no backend value yet).
  const syncFromServer = useCallback(async () => {
    if (!playlistId) return;
    setLoading(true);
    try {
      const response = await accountPlaylistToggleStore.getState(playlistId, {
        is_account_playlist: initialIsAccountPlaylist,
        cooldown_ends_at: cooldownEndsAt,
      });
      applyState(response);
    } catch (error) {
      Notification({ type: 'error', text: ACCOUNT_PLAYLIST_ERROR_MESSAGE });
    } finally {
      setLoading(false);
    }
  }, [applyState, cooldownEndsAt, initialIsAccountPlaylist, playlistId]);

  useEffect(() => {
    syncFromServer();
  }, [syncFromServer]);

  useEffect(() => {
    // Push state changes upward so card/detail can react (disable actions, etc).
    notifyParent({
      isAccountPlaylist: isChecked,
      isCoolingDown: cooldownActive,
      cooldownEndsAt,
      cooldownSecondsRemaining,
      isLoading: loading,
    });
  }, [cooldownActive, cooldownEndsAt, cooldownSecondsRemaining, isChecked, loading, notifyParent]);

  // Handles optimistic toggle while honoring cooldown/read-only rules.
  const handleChange = async (enabled: boolean) => {
    if (!playlistId) return;
    if (loading || cooldownActive || !editable) return;

    const previousValue = isChecked;
    setLoading(true);
    try {
      const response = await accountPlaylistToggleStore.setState({
        playlistId,
        enabled,
      });
      applyState(response);
      const successMessage = response.is_account_playlist
        ? ACCOUNT_PLAYLIST_MESSAGES.assigned
        : ACCOUNT_PLAYLIST_MESSAGES.unassigned;
      Notification({ type: 'success', text: successMessage, singleLine: true });
      await onRefresh?.();
    } catch (error) {
      setIsChecked(previousValue);
      Notification({ type: 'error', text: ACCOUNT_PLAYLIST_ERROR_MESSAGE });
    } finally {
      setLoading(false);
    }
  };

  const nonClickable = !editable || cooldownActive || loading;
  const label = isChecked ? labelOn : labelOff;

  const tooltipText = useMemo(() => {
    if (!editable && readOnly) return ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.readOnly;
    if (cooldownActive)
      return ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.cooldown(Math.max(1, cooldownSecondsRemaining));
    if (loading) return ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.loading;
    return isChecked
      ? ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.assignedInfo
      : ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.assignInfo;
  }, [cooldownActive, cooldownSecondsRemaining, editable, isChecked, loading, readOnly]);

  const ariaLabel = isChecked ? 'Playlist assigned to account' : 'Assign playlist to account';

  return (
    <Wrapper
      className={className}
      $nonClickable={nonClickable}
      aria-disabled={nonClickable}
      // Avoid triggering parent card click targets.
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip
        destroyTooltipOnHide
        placement="top"
        overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
        title={tooltipText || null}
      >
        <Row>
          <ToggleSwitch
            aria-label={ariaLabel}
            checked={isChecked}
            loading={loading}
            $nonClickable={nonClickable}
            onChange={nonClickable ? undefined : (value) => handleChange(value)}
            className="account-playlist__toggle"
          />
          <Label>{label}</Label>
        </Row>
      </Tooltip>
    </Wrapper>
  );
};

export const AccountPlaylistBadge = ({ className }: { className?: string }): JSX.Element => {
  return (
    <Tooltip
      title={ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.badge}
      overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
    >
      <Badge
        className={className}
        aria-label="Playlist assigned by your organization’s admin"
        role="status"
      >
        <BadgeIcon icon="AccountPlaylistBadge" />
      </Badge>
    </Tooltip>
  );
};

export default AccountPlaylistToggle;
